/**
 * @fileOverview Core data models and interfaces for API communication and type safety
 * @description Defines TypeScript interfaces and classes that serve as contracts for
 * API responses, ensuring type safety across the application. Provides templates for
 * both single object and array responses from backend endpoints.
 * 
 * @module model
 */

/**
 * @interface ApiModel
 * @template M - Generic type extending Model base class, representing the data entity type
 * @description Generic interface for standard API responses containing a single data object.
 * Defines the contract for responses from backend endpoints that return one entity or object.
 * 
 * The interface accommodates multiple response patterns used across different API endpoints,
 * with optional fields for various response types (tokens, device data, lists, etc.).
 * This flexibility allows the interface to handle diverse API response structures.
 * 
 * Typical Usage:
 * - User authentication responses (includes tokens)
 * - Single resource fetch operations (returns data object)
 * - Device/subscription detail endpoints
 * - Status/count queries
 * 
 * @property {boolean} status - HTTP status indicator (true for success, false for error)
 * @property {boolean} succes - Redundant status flag (likely typo in API, kept for compatibility)
 * @property {M} data - The main data payload containing the response entity
 * @property {IToken} tokens - Authentication tokens returned on login (optional, only in auth responses)
 * @property {M} deviceMovement - Device movement/activity data (optional, device-specific endpoints)
 * @property {M} count - Count/statistics data (optional, count query endpoints)
 * @property {M} devicesList - List of device objects (optional, wrapped as single object)
 * @property {M} results - Alternative data container (optional, alternative response format)
 * @property {any} [message] - Optional error or informational message from API
 * @property {M} [batchDetails] - Optional batch operation details
 * @property {M} result - Alternative main data container (optional, some endpoints use this)
 * 
 * @example
 * // Typing a user login response
 * interface UserData extends Model {
 *   email: string;
 *   name: string;
 * }
 * 
 * const response: ApiModel<UserData> = {
 *   status: true,
 *   succes: true,
 *   data: { id: '1', email: 'user@example.com', name: 'John' },
 *   tokens: { token: 'jwt...', refreshtoken: 'refresh...' },
 *   message: 'Login successful'
 * };
 */
export interface ApiModel<M extends Model> {
  status: boolean;
  succes: boolean;
  data: M;
  tokens: IToken;
  deviceMovement: M;
  count: M;
  devicesList: M;
  results: M;
  message?: any;
  batchDetails?: M;
  result: M;
}

/**
 * @interface ApiArrayModel
 * @template M - Generic type extending Model base class, representing the array item data type
 * @description Generic interface for API responses containing arrays of data objects.
 * Defines the contract for responses from backend endpoints that return multiple entities/objects.
 * 
 * The interface supports multiple response patterns for array data, accommodating various
 * API endpoint response structures. Different properties serve specific use cases.
 * 
 * Typical Usage:
 * - List/pagination endpoints (users, devices, subscriptions)
 * - Search results with multiple matches
 * - Image batch uploads
 * - Provider/business hierarchies
 * - Device movement history
 * 
 * @property {Array<M>} image_data - Array of image data objects (file upload responses)
 * @property {string} filename - Single filename returned with image data
 * @property {string} File_name - Alternative filename property (compatibility variant)
 * @property {boolean} status - HTTP status indicator (true for success, false for error)
 * @property {boolean} succes - Redundant status flag (likely typo in API, kept for compatibility)
 * @property {Array<M>} data - The main data payload containing array of response entities
 * @property {Array<M>} images - Array of image objects (alternative images property)
 * @property {any} [message] - Optional error or informational message from API
 * @property {Array<M>} count - Array of count/statistics objects (optional, analytics endpoints)
 * @property {Array<M>} devicesList - Array of device objects (optional, device list endpoints)
 * @property {Array<M>} results - Alternative data container (optional, alternative response format)
 * @property {Array<M>} deviceMovement - Array of device movement/activity objects (optional)
 * @property {Array<M>} providerBusinessPersons - Array of provider business person objects (optional)
 * @property {Array<M>} result - Alternative main data container (optional, some endpoints use this)
 * 
 * @example
 * // Typing a device list API response
 * interface Device extends Model {
 *   name: string;
 *   status: string;
 *   location: string;
 * }
 * 
 * const response: ApiArrayModel<Device> = {
 *   status: true,
 *   succes: true,
 *   data: [
 *     { id: '1', name: 'Device 1', status: 'active', location: 'Farm A', createdAt: new Date() },
 *     { id: '2', name: 'Device 2', status: 'inactive', location: 'Farm B', createdAt: new Date() }
 *   ],
 *   devicesList: [],
 *   results: [],
 *   message: 'Devices retrieved successfully'
 * };
 */
export interface ApiArrayModel<M extends Model> {
  image_data: Array<M>;
  filename: string;
  File_name: string;
  status: boolean;
  succes: boolean;
  data: Array<M>;
  images: Array<M>;
  message?: any;
  count: Array<M>;
  devicesList: Array<M>;
  results: Array<M>;
  deviceMovement: Array<M>;
  providerBusinessPersons: Array<M>;
  result: Array<M>;
}

/**
 * @class Model
 * @description Base class providing common properties for all data entities in the application.
 * Serves as the parent type for all domain models, ensuring consistent tracking of entity
 * lifecycle (creation, updates) through timestamps and unique identification.
 * 
 * This abstract base class should be extended by all domain-specific model classes
 * to provide type safety and consistency across the application.
 * 
 * Typical Usage:
 * - Extend this class in User, Device, Subscription models
 * - Used with ApiModel<M extends Model> for type-safe API responses
 * - Enables generic API response handling with known properties
 * 
 * @property {string} id - Unique identifier for the entity. Typically a UUID or database primary key.
 * Used to uniquely identify and reference entities throughout the application.
 * @property {Date} createdAt - Timestamp indicating when the entity was created.
 * Automatically set by the backend upon entity creation.
 * @property {Date} updatedAt - Timestamp indicating the last modification time of the entity.
 * Updated by the backend each time the entity is modified.
 * 
 * @example
 * // Extending Model for custom entities
 * class User extends Model {
 *   email: string;
 *   firstName: string;
 *   lastName: string;
 *   isActive: boolean;
 * }
 * 
 * // Using in API response
 * class UserService {
 *   getUser(id: string): Promise<ApiModel<User>> {
 *     return this.http.get<ApiModel<User>>(`/api/users/${id}`).toPromise();
 *   }
 * }
 */
export default class Model {
  id: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * @class IToken
 * @description Data class representing authentication token pair returned from login endpoint.
 * Contains both primary access token and refresh token for implementing token refresh
 * flows and maintaining authenticated sessions.
 * 
 * Responsibilities:
 * - Store authentication tokens received from backend
 * - Provide token data structure for storage and retrieval
 * - Support token-based authentication mechanism
 * 
 * Token Types:
 * - token: JWT access token used for authenticated API requests. Usually short-lived (15min-1hr).
 * - refreshtoken: Refresh token used to obtain new access tokens. Usually long-lived (days-weeks).
 * 
 * Typical Usage:
 * - Store in localStorage or sessionStorage after login
 * - Send token in Authorization header for authenticated requests
 * - Use refreshtoken to obtain new token when access token expires
 * 
 * @property {string} token - Primary JWT access token for authenticating API requests.
 * Should be included in Authorization header as "Bearer {token}" for protected endpoints.
 * @property {string} refreshtoken - Long-lived refresh token for obtaining new access tokens.
 * Typically stored securely and used only when access token expires.
 * 
 * @example
 * // Storing tokens after login
 * const tokenData: IToken = response.tokens;
 * localStorage.setItem('token', tokenData.token);
 * localStorage.setItem('refreshtoken', tokenData.refreshtoken);
 * 
 * // Using token in API requests
 * const headers = {
 *   'Authorization': `Bearer ${localStorage.getItem('token')}`
 * };
 */
export class IToken {
  token: string;
  refreshtoken: string;
  constructor() {
  }
}
